import moment from 'moment';
import momentTimezone from 'moment-timezone';
import {VNode} from 'snabbdom';
import {jsx} from 'panel';

import {TooltipEl} from 'common/components/tooltip';
import {reporter} from 'common/report/error-reporting';
import type {CurrentProjectStore} from 'common/report/stores/project-details';
import * as colors from 'common/util/colors';
import {getCurrentMoment, parseDate} from 'common/util/date';
import {pluralize} from 'common/util/string';
import {CALENDAR_DATE_FORMAT, DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DatePickerMode, getDatePickerMode} from 'common/widgets/date-range-picker-new/util';
import {Placement} from 'common/components/tooltip/types';

function getAbbreviatedTimezoneAsOf(date: moment.Moment, timezone: null | string): null | string {
  try {
    return momentTimezone.tz.zone(timezone).abbr(date.valueOf());
  } catch (err) {
    // report the error but don't die because we couldn't parse a timezone
    reporter.error(err);
    return null;
  }
}

export function renderDateRangeTooltip(
  dateRange: DateRange,
  currentProjectStore: CurrentProjectStore,
  placement: Placement = Placement.Top,
): VNode {
  return (
    <TooltipEl
      attrs={{placement, elref: `dateRangeTooltip`}}
      props={{
        // lazy load this date info because although the date parsing logic is not noticeably slow by itself (maybe ~5ms),
        // it adds up when there's multiple tooltip elements on the page.
        lazyContentRenderer: () => {
          const pickerMode = getDatePickerMode(dateRange);
          if (pickerMode === DatePickerMode.Between) {
            // add +1 to getIntervalDays() because we're showing the full span of the date range, which includes both From & To dates
            const daySpan = dateRange.getIntervalDays() + 1;

            return <>{`${daySpan} ${pluralize(`day`, daySpan)}`}</>;
          }

          const projectAsOfDate: null | moment.Moment = (() => {
            if (currentProjectStore.value?.projectAsOfDate) {
              const parsedDate = parseDate(currentProjectStore.value.projectAsOfDate);
              if (!parsedDate) {
                throw new Error(`"projectAsOfDate" does not contain a valid date string`);
              }
              return moment(parsedDate);
            }
            return null;
          })();
          const {from, to} = dateRange.getCalendarDatesAsOf(
            (projectAsOfDate ?? getCurrentMoment({timezone: currentProjectStore.value?.timezone})).format(
              CALENDAR_DATE_FORMAT,
            ),
          );
          const toDate = moment(parseDate(to));
          let fromDate = moment(parseDate(from));

          // for cases where `from` is not defined and asOfDate is past toDate
          if (fromDate.isAfter(toDate)) {
            fromDate = toDate;
          }

          const isSameDate = fromDate.isSame(toDate, `day`);

          return (
            <div style={{'text-align': `center`}}>
              <div>
                <span>
                  {isSameDate || !toDate.isValid()
                    ? fromDate.format(`MMM D, YYYY`)
                    : `${fromDate.format(`MMM D, YYYY`)} \u002D ${toDate.format(`MMM D, YYYY`)}`}
                </span>
                &nbsp;
                {currentProjectStore.value ? (
                  <span style={{color: colors.gray3}}>{`(${getAbbreviatedTimezoneAsOf(
                    toDate,
                    currentProjectStore.value.timezone,
                  )})`}</span>
                ) : null}
              </div>
              {projectAsOfDate ? <div>Data last imported {projectAsOfDate.format(`MMM D, YYYY`)}</div> : null}
            </div>
          );
        },
      }}
    />
  );
}
